/**
 * Reseller locator: sort, search and state filtering.
 *
 * Everything here reads the data-* attributes Liquid already wrote onto each
 * card, so it is completely indifferent to how many resellers exist or whether
 * they came from a metaobject or from theme blocks. Adding a reseller in the
 * admin needs no change to this file.
 */

/** @param {HTMLElement} root */
function initLocator(root) {
  const grid = root.querySelector('[data-phs-loc-grid]');
  const search = root.querySelector('[data-phs-loc-search]');
  const state = root.querySelector('[data-phs-loc-state]');
  const count = root.querySelector('[data-phs-loc-count]');
  const empty = root.querySelector('[data-phs-loc-empty]');
  const clears = [...root.querySelectorAll('[data-phs-loc-clear]')];
  if (!grid) return;

  const cards = [...grid.querySelectorAll('[data-phs-loc-card]')];

  // Sort once, in the DOM: state (conventional AU order), then suburb, then
  // business. Doing it here rather than in Liquid means a metaobject record
  // lands in the right place without relying on admin entry order.
  cards
    .sort((a, b) => {
      const ra = Number(a.dataset.rank) - Number(b.dataset.rank);
      if (ra) return ra;
      const sa = a.dataset.suburb.localeCompare(b.dataset.suburb);
      if (sa) return sa;
      return a.dataset.business.localeCompare(b.dataset.business);
    })
    .forEach((card) => grid.appendChild(card));

  /**
   * Short queries match on word boundaries, longer ones on substring.
   *
   * Plain substring matching made "WA" return six results — it is inside
   * "Waverley" and "Bayswater". Anything up to three characters is almost
   * always a state code, so those are anchored; longer queries stay loose so
   * partial suburb typing ("ballar") still works.
   */
  const matcher = (q) => {
    if (q.length > 3) return (hay) => hay.includes(q);
    const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`\\b${escaped}\\b`);
    return (hay) => re.test(hay);
  };

  const apply = () => {
    const q = (search?.value || '').trim().toLowerCase();
    const st = state?.value || '';
    const test = q ? matcher(q) : null;
    let shown = 0;

    for (const card of cards) {
      // Search and state are independent conditions, both must hold.
      const matchesState = !st || card.dataset.code === st;
      const matchesQuery = !q || test(card.dataset.search);
      const visible = matchesState && matchesQuery;
      card.hidden = !visible;
      if (visible) shown += 1;
    }

    if (count) {
      count.textContent = `${shown} Reseller Location${shown === 1 ? '' : 's'}`;
    }
    if (empty) empty.hidden = shown !== 0;
    grid.hidden = shown === 0;

    const filtering = Boolean(q || st);
    clears.forEach((btn) => {
      // The button inside the empty state is part of that panel's own copy, so
      // it should not be toggled by whether a filter is active.
      if (btn.closest('[data-phs-loc-empty]')) return;
      btn.hidden = !filtering;
    });
  };

  const reset = () => {
    if (search) search.value = '';
    if (state) state.value = '';
    apply();
    search?.focus();
  };

  search?.addEventListener('input', apply);
  state?.addEventListener('change', apply);
  clears.forEach((btn) => btn.addEventListener('click', reset));

  apply();
}

document.querySelectorAll('[data-phs-locator]').forEach(initLocator);
